import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { RecordingItem } from './RecordingItem';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/EmptyState';
import { PhoneOff } from 'lucide-react';

const fetchRecordings = async () => {
  try {
    const { data, error } = await supabase
      .from('calie_recordings')
      .select(`
        id,
        created_at,
        url,
        twilio_recording_sid,
        calie_calls!inner ( from_number, to_number, duration_sec )
      `)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Database error fetching recordings:', error);
      throw new Error(`Failed to load recordings: ${error.message}`);
    }
    
    return data;
  } catch (error) {
    console.error('Unexpected error in fetchRecordings:', error);
    throw error;
  }
};

// Note the change to a named export
export const CallRecordingsList = () => {
  const { data: recordings, isLoading, error } = useQuery({
    queryKey: ['recordings'],
    queryFn: fetchRecordings,
    retry: (failureCount, error) => {
      return failureCount < 2; // Retry up to 2 times
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    console.error('CallRecordingsList error:', error);
    return (
      <div className="p-4 border border-destructive/20 rounded-lg bg-destructive/5">
        <p className="text-destructive font-medium">Error loading recordings</p>
        <p className="text-sm text-muted-foreground mt-1">{error.message}</p>
      </div>
    );
  }

  if (!recordings || recordings.length === 0) {
    return <EmptyState icon={<PhoneOff className="h-8 w-8 text-muted-foreground" />} title="No Recordings Found" description="When calls are recorded, they will appear here." />;
  }

  return (
    <div className="space-y-4">
      {recordings.map((recording: any) => (
        <RecordingItem key={recording.id} recording={recording} />
      ))}
    </div>
  );
};